import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, MantineProvider } from '@mantine/core';
import { useEffect, useState } from 'react';

// Dashboard Comercial Layout & Pages
import DCLayout from './components/DashboardComercial/DCLayout';
import CapTd from './components/DashboardComercial/CapTd';
import TdIti from './components/DashboardComercial/TdIti';
import Foc from './components/DashboardComercial/Foc';
import CargaArchivos from './components/DashboardComercial/CargaArchivos';
import Comercial from './components/DashboardComercial/Comercial';
import DashboardV2 from './components/DashboardComercial/DashboardV2';
import GlobalLoaderProvider from './components/DashboardComercial/GlobalLoader';
import Login from './components/Login/page';

// SSCN Front (ProtectedRoutes)
import ProtectedRoute from './components/ProtectedRoutes/page';
import Home from './components/Home/page';
import Conciliaciones from './components/Conciliaciones/page';
import Reportes from './components/Reportes/page';
import ReportesRDI from './components/ReportesRDI/page';
import CifrasRDI from './components/CifrasRDI/page';
import ErrorPage from './components/ErrorPage/page';

import AppContextProvider from './context/context';
import { Notifications } from '@mantine/notifications';
import { authService } from './services/auth/authService';
import { useAuth } from './components/DashboardComercial/useAuth';

import userService from './services/userService';

import '@mantine/notifications/styles.css';
import './App.css';

const theme = createTheme({});

// ============================================================
// COMPONENTE: Login Redirect (para usuarios no autorizados)
// ============================================================
function ConciliacionesApp() {
  return (
    <MantineProvider theme={theme} withCSSVariables>
      <Notifications position="top-right" zIndex={999} />
      <AppContextProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/Home" replace />} />
           <Route element={<ProtectedRoute />}> 
              <Route path="/Home" element={<Home />} />
              <Route path="/Conciliaciones" element={<Conciliaciones />} />
              <Route path="/Reportes" element={<Reportes />} />
              <Route path="/ReportesRDI" element={<ReportesRDI />} />
              <Route path="/CifrasRDI" element={<CifrasRDI />} />
            </Route> 
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </BrowserRouter>
      </AppContextProvider>
    </MantineProvider>
  );
}

// ============================================================
// COMPONENTE: Dashboard Comercial App (usuarios autorizados)
// ============================================================
function DashboardComercialApp() {
  return (
    <MantineProvider theme={theme} withCSSVariables>
      <Notifications position="top-right" zIndex={999} />
      <AppContextProvider>
        <GlobalLoaderProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Navigate to="/dc" replace />} />
              <Route path="/Home" element={<Navigate to="/dc" replace />} />
              <Route element={<DCLayout />}>
                <Route path="/dc" element={<DashboardV2 />} />
                <Route path="/dc/carga" element={<CargaArchivos />} />
                <Route path="/dc/cap-td" element={<CapTd />} />
                <Route path="/dc/td-iti" element={<TdIti />} />
                <Route path="/dc/fco" element={<Foc />} />
                <Route path="/dc/comercial" element={<Comercial />} />
              </Route>
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </BrowserRouter>
        </GlobalLoaderProvider>
      </AppContextProvider>
    </MantineProvider>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL: Aplicación unificada
// ============================================================
function App() {

  const { isAuthenticated, isLoading, user } = useAuth();
  const [ready, setReady] = useState(false);

  // Esperar a que la autenticación esté lista
  useEffect(() => {
    if (!isLoading) {
      // En modo dev, estar listo inmediatamente
      if (authService.isDevMode()) {
        setReady(true);
        return;
      }
      
      // Timeout de seguridad para auth0
      const timeout = setTimeout(() => setReady(true), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

/*

  // Mostrar nada mientras carga
  if (!ready || isLoading) {
    return null;
  }

  // En modo dev, siempre mostrar Dashboard (sin login)
  if (authService.isDevMode()) {
    return <DashboardComercialApp />;
  }

  // Modo Auth0: verificar autenticación
  if (!isAuthenticated) {
    return (
      <MantineProvider theme={theme}>
        <Login />
      </MantineProvider>
    );
  }


*/


  if (!ready || isLoading) {
    return null;
  }

  // DEV y AUTH0 siguen exactamente el mismo flujo:
  // si no está autenticado, mostrar Login.
  if (!isAuthenticated) {
    return (
      <MantineProvider theme={theme}>
        <Login />
      </MantineProvider>
    );
  }

  // Verificar autorización
  const userEmail = (user?.email || '').toLowerCase();
  const isAuthorized = userService.isDashboardCommercialUser(userEmail);

  // Si no está autorizado, mostrar la app básica (sin Dashboard)
  if (!isAuthorized) {
    return <ConciliacionesApp />;
  }
  
  // Usuario autenticado y autorizado: mostrar Dashboard
  return <DashboardComercialApp />;
}

export default App;
